export type ShareCardData = {
  clarity: number;
  durationSec: number;
  itemsCleared: number;
  waveform: number[];
  note?: string;
};

export type ShareOutcome = "shared" | "copied" | "downloaded" | "failed";

/** Renders the session to an offscreen canvas at 2x, then tries Web Share
    (with files), falls back to clipboard, falls back to a plain download. */
export async function shareSessionCard(data: ShareCardData): Promise<ShareOutcome> {
  const canvas = renderCard(data);
  let blob: Blob;
  try {
    blob = await canvasToBlob(canvas);
  } catch {
    return "failed";
  }

  const nav = navigator as Navigator & { canShare?: (data: { files: File[] }) => boolean };
  if (nav.share && nav.canShare) {
    const file = new File([blob], "diction-dojo-session.png", { type: "image/png" });
    if (nav.canShare({ files: [file] })) {
      try {
        await nav.share({ files: [file], title: "Diction Dojo session" });
        return "shared";
      } catch {
        /* user cancelled, or share failed — fall through */
      }
    }
  }

  const clip = navigator.clipboard as Clipboard & { write?: (items: ClipboardItem[]) => Promise<void> };
  if (clip?.write && typeof ClipboardItem !== "undefined") {
    try {
      await clip.write([new ClipboardItem({ "image/png": blob })]);
      return "copied";
    } catch {
      /* fall through to download */
    }
  }

  downloadBlob(blob, "diction-dojo-session.png");
  return "downloaded";
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png");
  });
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, lineHeight: number): number {
  const words = text.split(" ");
  let line = "";
  let cy = y;
  for (const word of words) {
    const test = line + word + " ";
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, cy);
      line = word + " ";
      cy += lineHeight;
    } else {
      line = test;
    }
  }
  ctx.fillText(line, x, cy);
  return cy + lineHeight;
}

function renderCard(data: ShareCardData): HTMLCanvasElement {
  const scale = 2;
  const width = 600 * scale;
  const height = 760 * scale;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;

  ctx.fillStyle = "#0E0F0A";
  ctx.fillRect(0, 0, width, height);

  // waveform trace
  const wf = data.waveform.length ? data.waveform : [1];
  const max = Math.max(...wf, 1);
  const midY = 200 * scale;
  const ampl = 90 * scale;
  const padX = 40 * scale;
  const stepX = (width - padX * 2) / Math.max(1, wf.length - 1);
  ctx.strokeStyle = "#D6F24B";
  ctx.lineWidth = 3 * scale;
  ctx.beginPath();
  wf.forEach((v, i) => {
    const x = padX + i * stepX;
    const dir = i % 2 === 0 ? 1 : -1;
    const y = midY - (v / max) * ampl * dir;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();

  ctx.fillStyle = "#F7F7F0";
  ctx.textAlign = "left";
  ctx.font = `${58 * scale}px Georgia, serif`;
  ctx.fillText(`+${data.clarity} Clarity`, padX, 330 * scale);

  ctx.font = `${18 * scale}px monospace`;
  ctx.fillStyle = "#8F937F";
  const minutes = Math.max(1, Math.round(data.durationSec / 60));
  ctx.fillText(`${data.itemsCleared} items · ${minutes} min`, padX, 366 * scale);

  if (data.note) {
    ctx.font = `${22 * scale}px Georgia, serif`;
    ctx.fillStyle = "#D6F24B";
    wrapText(ctx, data.note, padX, 430 * scale, width - padX * 2, 30 * scale);
  }

  ctx.font = `${16 * scale}px monospace`;
  ctx.fillStyle = "#454936";
  ctx.fillText("DICTION DOJO", padX, height - 32 * scale);

  return canvas;
}
